import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { ShareFillIndicator } from '../components/ShareFillIndicator';
import { PriceChart } from '../components/PriceChart';
import { ReserveSharesPanel } from '../components/ReserveSharesPanel';
import { StatusBadge } from '../components/StatusBadge';
import { VisualTruckOwnership, VisualTrailerOwnership } from '../components/VisualOwnership';
import { ROICalculator } from '../components/ROICalculator';
import { PhotoGalleryModal } from '../components/PhotoGalleryModal';
import { assetsAPI, requestsAPI, portfolioAPI, performanceAPI } from '../lib/api';
import { formatCurrency, formatDateTime, formatNumber } from '../lib/formatters';
import { ArrowLeft, Truck, Container, Camera, TrendingUp, DollarSign, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

export default function AssetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reserving, setReserving] = useState(false);
  const [userShares, setUserShares] = useState(0);
  const [onSaleShares, setOnSaleShares] = useState(0);
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);

  useEffect(() => {
    fetchAssetDetails();
  }, [assetId]);

  const fetchAssetDetails = async () => {
    try {
      setLoading(true);
      const [assetResponse, historyResponse, portfolioResponse] = await Promise.all([
        assetsAPI.get(assetId),
        assetsAPI.getPriceHistory(assetId),
        portfolioAPI.get(),
      ]);
      setAsset(assetResponse.data);
      setPriceHistory(historyResponse.data);
      
      // Get user's shares count for this asset
      const ownedAsset = portfolioResponse.data.assets.find(
        (a) => a.asset.asset_id === assetId
      );
      if (ownedAsset) {
        setUserShares(ownedAsset.shares_owned || 0);
      }
      
      // Get on-sale shares (from marketplace listings) - for now use 0 as placeholder
      setOnSaleShares(assetResponse.data.on_sale_shares || 0);
      
      // Fetch performance summary for ROI calculator
      try {
        const perfResponse = await performanceAPI.getSummary(assetId);
        setPerformanceSummary(perfResponse.data);
      } catch (perfError) {
        console.log('No performance data available');
        setPerformanceSummary(null);
      }
    } catch (error) {
      console.error('Failed to fetch asset details:', error);
      toast.error('Failed to load asset details');
    } finally {
      setLoading(false);
    }
  };

  const handleReserve = async (shares) => {
    try {
      setReserving(true);
      await requestsAPI.create({
        asset_id: assetId,
        shares_requested: shares,
      });
      toast.success('Buy request submitted successfully!');
      // Refresh asset data
      fetchAssetDetails();
    } catch (error) {
      console.error('Failed to create request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setReserving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-96 bg-white border border-[hsl(var(--border))] rounded-xl" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 h-96 bg-white border border-[hsl(var(--border))] rounded-xl" />
              <div className="h-96 bg-white border border-[hsl(var(--border))] rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
            <p className="text-[hsl(var(--text-secondary))] text-lg">Asset not found</p>
          </div>
        </div>
      </div>
    );
  }

  const Icon = asset.type === 'truck' ? Truck : Container;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Back Button */}
        <button
          data-testid="back-button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))] mb-6 transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-medium">Back to Fleet</span>
        </button>

        {/* Hero Section */}
        <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden mb-6">
          {asset.image_url ? (
            <div className="relative h-64 md:h-96">
              <img
                src={asset.image_url}
                alt={asset.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-white text-sm font-medium px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                    {asset.type.toUpperCase()}
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {asset.name}
                </h1>
                <p className="text-white/90 font-mono text-sm">Unit #{asset.unit_number}</p>
              </div>
            </div>
          ) : (
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                  <Icon className="w-8 h-8 text-[hsl(var(--brand-primary))]" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))]">
                    {asset.name}
                  </h1>
                  <p className="text-[hsl(var(--text-secondary))] font-mono">#{asset.unit_number}</p>
                </div>
              </div>
            </div>
          )}

          {/* Price Banner */}
          <div className="p-6 md:p-8 border-t border-[hsl(var(--border))] bg-[hsl(var(--background-secondary))]">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-sm text-[hsl(var(--text-secondary))] mb-1">Current Share Price</div>
                <div className="text-4xl md:text-5xl font-bold font-mono text-[hsl(var(--brand-primary))]">
                  {formatCurrency(asset.current_price)}
                </div>
              </div>
              <StatusBadge status={asset.status} />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo Gallery Button */}
            {asset.image_url && (
              <button
                data-testid="view-photos-btn"
                onClick={() => setShowPhotoGallery(true)}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 text-white font-semibold rounded-xl transition-all duration-200"
              >
                <Camera className="w-5 h-5" />
                View Real Photos of This {asset.type === 'truck' ? 'Truck' : 'Trailer'}
              </button>
            )}

            {/* Visual Ownership Indicator - Always show */}
            {asset.type === 'truck' ? (
              <VisualTruckOwnership 
                totalShares={asset.total_shares}
                yourShares={userShares}
                onSaleShares={onSaleShares}
                soldShares={asset.sold_shares}
              />
            ) : (
              <VisualTrailerOwnership 
                totalShares={asset.total_shares}
                yourShares={userShares}
                onSaleShares={onSaleShares}
                soldShares={asset.sold_shares}
              />
            )}

            {/* Performance Summary */}
            {performanceSummary && (
              <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    <h3 className="font-semibold">Performance Summary (Last {performanceSummary.weeks_analyzed} Weeks)</h3>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-xs text-green-700 uppercase tracking-wide mb-1">Total Earnings</div>
                      <div className="text-xl font-bold font-mono text-green-800">{formatCurrency(performanceSummary.total_earnings)}</div>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <div className="text-xs text-red-700 uppercase tracking-wide mb-1">Total Expenses</div>
                      <div className="text-xl font-bold font-mono text-red-800">{formatCurrency(performanceSummary.total_expenses)}</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-xs text-blue-700 uppercase tracking-wide mb-1">Net Profit</div>
                      <div className="text-xl font-bold font-mono text-blue-800">{formatCurrency(performanceSummary.total_net_profit)}</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <div className="text-xs text-purple-700 uppercase tracking-wide mb-1">Avg Weekly</div>
                      <div className="text-xl font-bold font-mono text-purple-800">{formatCurrency(performanceSummary.avg_weekly_net)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ROI Calculator */}
            <ROICalculator
              sharePrice={asset.current_price}
              avgWeeklyProfit={performanceSummary?.avg_weekly_net || 0}
              totalShares={asset.total_shares}
              assetName={asset.name}
            />

            {/* Share Fill Indicator */}
            <ShareFillIndicator totalShares={asset.total_shares} soldShares={asset.sold_shares} />

            {/* Price Chart */}
            <PriceChart data={priceHistory} />

            {/* Description */}
            {asset.description && (
              <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
                <h3 className="text-lg font-semibold text-[hsl(var(--text-primary))] mb-3">
                  Description
                </h3>
                <p className="text-[hsl(var(--text-secondary))]">{asset.description}</p>
              </div>
            )}

            {/* Asset Specifications */}
            <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-[hsl(var(--text-primary))] mb-4">
                Asset Specifications
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Asset Type</div>
                  <div className="text-sm font-medium text-[hsl(var(--text-primary))]">
                    {asset.type.toUpperCase()}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Unit Number</div>
                  <div className="text-sm font-medium font-mono text-[hsl(var(--text-primary))]">
                    {asset.unit_number}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Total Shares</div>
                  <div className="text-sm font-medium font-mono text-[hsl(var(--text-primary))]">
                    {asset.total_shares}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Shares Sold</div>
                  <div className="text-sm font-medium font-mono text-[hsl(var(--text-primary))]">
                    {asset.sold_shares}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Created</div>
                  <div className="text-sm font-medium text-[hsl(var(--text-primary))]">
                    {formatDateTime(asset.created_at)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[hsl(var(--text-secondary))] mb-1">Last Updated</div>
                  <div className="text-sm font-medium text-[hsl(var(--text-primary))]">
                    {formatDateTime(asset.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Reserve Panel */}
          <div className="lg:col-span-1">
            <ReserveSharesPanel asset={asset} onReserve={handleReserve} loading={reserving} />
          </div>
        </div>
      </main>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        images={asset?.image_url ? [asset.image_url] : []}
        assetName={asset?.name || 'Asset'}
        isOpen={showPhotoGallery}
        onClose={() => setShowPhotoGallery(false)}
      />
    </div>
  );
}